import { EyeOutlined } from '@ant-design/icons';
import { Box, Flex, Heading } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { Asset, InfoSlideItemType } from '@llx/common';
import parse from 'html-react-parser';
import { AnimatePresence, motion } from 'motion/react';
import { zoomIn } from 'react-animations';

import HostIsHereAnimation from '@/components/HostImageWrapper/HostImageWrapper';
import ImageWithLoader from '@/components/ImageWithLoader/ImageWithLoader';
import { getAssetByRefOrThrow } from '@/pages/caseCluster/utils/getAssetByRef';
import APP_CONSTANTS from '@/utils/constants/app.constants';

const startAnimation = keyframes`${zoomIn}`;

type Props = {
  item: InfoSlideItemType;
  assetList: Asset[];
  containsHostAsset: boolean;
  onMoreInfoCallback: (item: InfoSlideItemType) => void;
};

const MotionBox = motion.create(Box);

const InfoItem = ({ item, assetList, containsHostAsset, onMoreInfoCallback }: Props) => {
  const thumbUrl =
    item.assetRefs.length > 0
      ? getAssetByRefOrThrow(item.assetRefs[0], assetList).thumbUrl
      : undefined;

  return (
    <Box
      data-test="slide-item"
      onClick={() => item.assetRefs && onMoreInfoCallback?.(item)}
      height="100%"
      maxWidth="40vw"
      maxHeight="50vh"
      padding="0px"
      position="relative"
      animation={`0.5s ${startAnimation}`}
    >
      {
        //-- CASE Thumb
        thumbUrl ? (
          <>
            <Flex
              // NOTE: We are still using thumbContainer class because it is used in pdf generation & cypress
              className="thumbContainer"
              userSelect="none"
              width="100%"
              height="80%"
              borderRadius={2.5}
              border="1px solid #d8d8d8"
              overflow="hidden"
              backgroundColor="white"
              justifyContent="center"
              alignItems="center"
              cursor="pointer"
              _hover={{
                border: '1px solid',
                borderColor: 'primary',
              }}
            >
              <ImageWithLoader
                maxW="100%"
                maxH="100%"
                width="100%"
                height="100%"
                fit="contain"
                alt={item.title}
                src={thumbUrl}
              />
            </Flex>
            {/* // TODO: reset bookmark funcitonality when developped */}
            {/* <div className="bookmark" /> */}
            <Flex justifyContent="space-between" width="100%">
              <Flex flexDir="column">
                <Heading
                  color="livelinx.purple200"
                  padding="5px 35px 0px 5px"
                  // fontSize={{ base: '0.8em', md: '1.4em', lg: '0.8em' }}
                  fontSize="0.8em"
                  width="100%"
                  className="title"
                >
                  {item.title}
                </Heading>
                {item.description && (
                  <Box
                    color="livelinx.grey200"
                    padding="0px 5px 5px 5px"
                    fontSize="0.7em"
                    className="description"
                  >
                    {parse(item.description || '')}
                  </Box>
                )}
                <Box
                  color="livelinx.grey200"
                  padding="0px 5px 15px 5px"
                  fontSize="0.7em"
                  paddingBottom={2.5}
                  className="description"
                >
                  {item.assetRefs &&
                    item.assetRefs.length > 1 &&
                    `${item.assetRefs.length} documents`}
                  {item.assetRefs &&
                    item.assetRefs.length <= 1 &&
                    `${item.assetRefs.length} document`}
                </Box>
              </Flex>
              <AnimatePresence>
                {containsHostAsset && (
                  <MotionBox
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={
                      APP_CONSTANTS.SHOW_ANIMATIONS
                        ? { duration: 0.3, ease: 'easeInOut' }
                        : { duration: 0 }
                    }
                    maxH={10}
                    maxW={10}
                  >
                    <HostIsHereAnimation />
                  </MotionBox>
                )}
              </AnimatePresence>
            </Flex>
            <Box
              visibility="hidden"
              transition=" all 0.3s ease-out"
              position="absolute"
              top="0px"
              left="0px"
              width="100%"
              height="80%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="2em"
              color="primary"
              backgroundColor="#ffffff84"
              opacity="0"
              borderRadius={2.5}
              userSelect="none"
              _hover={{
                visibility: 'visible',
                opacity: 1,
              }}
            >
              <EyeOutlined />
            </Box>
          </>
        ) : (
          // -- CASE Text Only
          <Flex
            userSelect="none"
            height="100%"
            flexDir="column"
            justifyContent="center"
            alignItems="center"
          >
            <Box
              display="block"
              color="livelinx.purple200"
              borderRadius={5}
              height="auto"
              padding={5}
              textAlign="left"
              border="solid 1px #d8d8d8"
              width="100%"
            >
              <Heading color="livelinx.purple200" fontSize="1.2em" className="title">
                {item.title}
              </Heading>

              {item.description && (
                <Box
                  color="livelinx.grey200"
                  padding="5px 0 0 0"
                  fontSize="2vmin"
                  className="description"
                >
                  {parse(item.description || '')}
                </Box>
              )}
            </Box>
          </Flex>
        )
      }
    </Box>
  );
};

export default InfoItem;
